/**
 * วันหยุดแบบพนักงานลงเอง (self-service day-off) — Phase 1: หน้าตั้งค่าฝั่ง HR/OWNER.
 *
 * 3 ส่วน:
 *   1) รอบ & หน้าต่างจอง — เปิดให้ลงวันที่ 20–25, ล็อกวันที่ 26, รอบ 26→25
 *   2) โควตาต่อแผนก — จำนวนวันหยุด/รอบ (ไม่เท่ากันทุกแผนก)
 *   3) เพดานคนหยุด/วัน ต่อ "ตำแหน่ง" — คนตำแหน่งเดียวกันห้ามหยุดซ้อนเกินเพดาน
 *      (ระบบคิด auto = ceil(คนในตำแหน่ง × โควตา ÷ วันในรอบ); HR ตั้งเองทับได้)
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useDayoffConfig, usePutDayoffSettings, usePutDayoffDeptQuota, usePutDayoffPositionLimit, usePutDayoffPositionQuota,
  type DayoffSettingsRow, type DayoffDeptRow, type DayoffPositionRow,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { CalendarClock, Users, Briefcase } from 'lucide-react';

export function HRDayoff() {
  const { data, isLoading } = useDayoffConfig();

  return (
    <AdminLayout title="วันหยุด (พนักงานลงเอง)">
      {isLoading || !data ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : (
        <div className="space-y-4">
          <SettingsCard settings={data.settings} />
          <DeptQuotaCard departments={data.departments} />
          <PositionsCard positions={data.positions} cycleDays={data.cycle.days} />
        </div>
      )}
    </AdminLayout>
  );
}

// ── 1) รอบ & หน้าต่างจอง ──────────────────────────────────────────────────────
function SettingsCard({ settings }: { settings: DayoffSettingsRow }) {
  const put = usePutDayoffSettings();
  const toast = useToast();
  const [cycleStart, setCycleStart] = useState(settings.cycle_start_day);
  const [openDay, setOpenDay] = useState(settings.booking_open_day);
  const [lockDay, setLockDay] = useState(settings.booking_lock_day);
  const [enabled, setEnabled] = useState(settings.enabled);

  async function save() {
    try {
      await put.mutateAsync({
        cycle_start_day: cycleStart, booking_open_day: openDay,
        booking_lock_day: lockDay, enabled,
      });
      toast.success('บันทึกค่ารอบแล้ว');
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="w-5 h-5 text-brand-600" />
        <h2 className="font-semibold">รอบ & หน้าต่างจอง</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        รอบ {cycleStart}→{((cycleStart + 30) % 31) || 25} · เปิดให้พนักงานลงวันหยุดวันที่ {openDay}–{Math.max(openDay, lockDay - 1)} · ล็อกวันที่ {lockDay}
      </p>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="label">วันเริ่มรอบ</label>
          <input type="number" min={1} max={28} className="input" value={cycleStart}
            onChange={(e) => setCycleStart(Number(e.target.value))} />
        </div>
        <div>
          <label className="label">เปิดจองวันที่</label>
          <input type="number" min={1} max={28} className="input" value={openDay}
            onChange={(e) => setOpenDay(Number(e.target.value))} />
        </div>
        <div>
          <label className="label">ล็อกวันที่</label>
          <input type="number" min={1} max={28} className="input" value={lockDay}
            onChange={(e) => setLockDay(Number(e.target.value))} />
        </div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        เปิดใช้ระบบให้พนักงานลงวันหยุดเอง
      </label>
      <button onClick={save} disabled={put.isPending} className="btn-primary btn-slim mt-3">
        {put.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
      </button>
    </div>
  );
}

// ── 2) โควตาต่อแผนก ───────────────────────────────────────────────────────────
function DeptQuotaCard({ departments }: { departments: DayoffDeptRow[] }) {
  const put = usePutDayoffDeptQuota();
  const toast = useToast();
  const [edits, setEdits] = useState<Record<string, string>>({});

  async function save(deptId: string) {
    const raw = edits[deptId];
    const val = Number(raw);
    if (raw === undefined || raw === '' || Number.isNaN(val) || val < 0 || val > 31) {
      toast.error('โควตาต้องเป็นตัวเลข 0–31'); return;
    }
    try {
      await put.mutateAsync({ department_id: deptId, quota_per_cycle: val });
      toast.success('บันทึกโควตาแล้ว');
      setEdits((m) => { const n = { ...m }; delete n[deptId]; return n; });
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="w-5 h-5 text-brand-600" />
        <h2 className="font-semibold">โควตาวันหยุดต่อแผนก</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">จำนวนวันหยุด/รอบ ของพนักงานในแต่ละแผนก (ไม่เท่ากันได้)</p>
      {departments.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">ยังไม่มีแผนก — สร้างแผนกที่ "โครงสร้างองค์กร" ก่อน</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {departments.map((d) => {
            const editing = edits[d.id] !== undefined;
            const shown = editing ? edits[d.id] : (d.quota_per_cycle ?? '');
            return (
              <div key={d.id} className="flex items-center gap-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{d.name}</div>
                  {d.quota_per_cycle === null && <div className="text-[10px] text-amber-600">ยังไม่ตั้งโควตา</div>}
                </div>
                <input type="number" min={0} max={31} className="input w-20 text-center"
                  value={shown}
                  onChange={(e) => setEdits((m) => ({ ...m, [d.id]: e.target.value }))} />
                <span className="text-xs text-gray-400">วัน</span>
                <button onClick={() => save(d.id)} disabled={!editing || put.isPending}
                  className="btn-secondary btn-slim">บันทึก</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 3) เพดานคนหยุด/วัน ต่อตำแหน่ง ──────────────────────────────────────────────
function PositionsCard({ positions, cycleDays }: { positions: DayoffPositionRow[]; cycleDays: number }) {
  const put = usePutDayoffPositionLimit();
  const putQuota = usePutDayoffPositionQuota();
  const toast = useToast();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [quotaEdits, setQuotaEdits] = useState<Record<string, string>>({});

  async function save(pos: string) {
    const raw = (edits[pos] ?? '').trim();
    // เว้นว่าง = ล้าง (กลับไป auto)
    const val = raw === '' ? null : Number(raw);
    if (val !== null && (Number.isNaN(val) || val < 0 || val > 999)) {
      toast.error('เพดานต้องเป็นตัวเลข ≥ 0 (เว้นว่าง = อัตโนมัติ)'); return;
    }
    try {
      await put.mutateAsync({ position: pos, max_concurrent: val });
      toast.success(val === null ? 'กลับไปใช้เพดานอัตโนมัติ' : 'บันทึกเพดานแล้ว');
      setEdits((m) => { const n = { ...m }; delete n[pos]; return n; });
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  async function saveQuota(pos: string) {
    const raw = (quotaEdits[pos] ?? '').trim();
    // เว้นว่าง = ล้าง (กลับไปใช้โควตาแผนก)
    const val = raw === '' ? null : Number(raw);
    if (val !== null && (Number.isNaN(val) || val < 0 || val > 31)) {
      toast.error('โควตาต้องเป็นตัวเลข 0–31 (เว้นว่าง = ใช้ของแผนก)'); return;
    }
    try {
      await putQuota.mutateAsync({ position: pos, quota_per_cycle: val });
      toast.success(val === null ? 'กลับไปใช้โควตาของแผนก' : 'บันทึกโควตาตำแหน่งแล้ว');
      setQuotaEdits((m) => { const n = { ...m }; delete n[pos]; return n; });
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        <Briefcase className="w-5 h-5 text-brand-600" />
        <h2 className="font-semibold">โควตา + เพดานวันหยุด ต่อตำแหน่ง</h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        <b>โควตา</b> = จำนวนวันหยุด/รอบต่อคน (เว้นว่าง = ใช้ของแผนก) ·
        <b> เพดาน</b> = คนตำแหน่งเดียวกันหยุดซ้อนได้กี่คน/วัน (เว้นว่าง = auto: คน × โควตา ÷ {cycleDays} วัน)
      </p>
      {positions.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">ยังไม่มีตำแหน่ง — เพิ่มพนักงาน + ระบุตำแหน่งก่อน</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {positions.map((p) => {
            const editing = edits[p.position] !== undefined;
            const shown = editing ? edits[p.position] : (p.manual_cap ?? '');
            const effective = p.manual_cap ?? p.auto_cap;
            const qEditing = quotaEdits[p.position] !== undefined;
            const qShown = qEditing ? quotaEdits[p.position] : (p.position_quota ?? '');
            return (
              <div key={p.position} className="py-2 space-y-1.5">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {p.position}
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
                    <Users className="w-3 h-3" />{p.headcount}
                  </span>
                  <span className="text-[10px] text-gray-400 font-normal">· {p.department ?? '—'}</span>
                </div>
                {/* โควตา/รอบ ต่อตำแหน่ง */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 text-[10px] text-gray-500">
                    โควตาที่ใช้จริง <b>{p.quota ?? '—'}</b> วัน/รอบ
                    {p.position_quota != null ? ' (ตั้งตำแหน่ง)' : (p.dept_quota != null ? ' (ของแผนก)' : '')}
                    {p.quota == null && <span className="text-amber-600"> · ยังไม่ตั้งโควตา</span>}
                  </div>
                  <input type="number" min={0} max={31} placeholder={p.dept_quota != null ? `แผนก ${p.dept_quota}` : 'โควตา'}
                    className="input w-20 text-center text-sm" value={qShown}
                    onChange={(e) => setQuotaEdits((m) => ({ ...m, [p.position]: e.target.value }))} />
                  <button onClick={() => saveQuota(p.position)} disabled={!qEditing || putQuota.isPending}
                    className="btn-secondary btn-slim">โควตา</button>
                </div>
                {/* เพดานคน/วัน ต่อตำแหน่ง */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 text-[10px] text-gray-500">
                    เพดานที่ใช้จริง <b>{effective || '∞'}</b> คน/วัน{p.manual_cap == null ? ' (auto)' : ' (ตั้งเอง)'}
                  </div>
                  <input type="number" min={0} max={999} placeholder={`auto ${p.auto_cap}`}
                    className="input w-20 text-center text-sm" value={shown}
                    onChange={(e) => setEdits((m) => ({ ...m, [p.position]: e.target.value }))} />
                  <button onClick={() => save(p.position)} disabled={!editing || put.isPending}
                    className="btn-secondary btn-slim">เพดาน</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
